import { Box } from '@mui/material';
import { Button, FlexContainer, ReemaThemeV2 } from '@act/storybook';
import {
  CallDto,
  ConsentGoalDto,
  SurveyV2,
  UserDto,
} from '@act/shared/data-transfer-objects';
import { Goal } from 'stories/containers/GoalsTracking/types';
import { GoalsPanelContent } from './GoalsPanelContent';
import { NeedDetailContent } from '@act/react-components/needs/need-detail-dialog/NeedDetailContent';
import { LoadingStatus } from 'redux/types/shared-types';
import { SurveyDrawer } from '@act/react-components/dialog-forms/components/SurveyDrawer';
import { ConsentDrawer } from './ConsentDrawer';
import { AddNeedContent } from '@act/react-components/needs/need-detail-dialog/AddNeedContent';
import { TrackingProvider } from '@act/react-components/tracking';
import { useState, useEffect } from 'react';

interface CallGoalsPanelProps {
  selectedMember: UserDto | undefined;
  goals: Goal[];
  goalsLoadingStatus: LoadingStatus;
  selectedNeedId: number | undefined;
  onGoalClick: (goal: Goal) => void;
  onSaveClick: () => void;
  setSelectedSurvey: (survey: SurveyV2.AssignedSurveyDto | undefined) => void;
  selectedSurvey: SurveyV2.AssignedSurveyDto | undefined;
  selectedConsent: ConsentGoalDto | undefined;
  setSelectedConsent: (consent: ConsentGoalDto | undefined) => void;
  setOpenAddNeed: (open: boolean) => void;
  openAddNeed: boolean;
  call: CallDto | undefined;
  setSelectedNeedId: (needId: number | undefined) => void;
}

export const CallGoalsPanel = ({
  selectedMember,
  goals,
  goalsLoadingStatus,
  selectedNeedId,
  onGoalClick,
  setSelectedNeedId,
  onSaveClick,
  setSelectedSurvey,
  selectedSurvey,
  selectedConsent,
  setSelectedConsent,
  setOpenAddNeed,
  openAddNeed,
}: CallGoalsPanelProps) => {
  const [shouldRenderNeed, setShouldRenderNeed] = useState(!!selectedNeedId);
  const [needIdForAnimation, setNeedIdForAnimation] = useState<
    number | undefined
  >(selectedNeedId);
  const [shouldRenderSurvey, setShouldRenderSurvey] = useState(
    !!selectedSurvey
  );
  const [surveyForAnimation, setSurveyForAnimation] = useState<
    SurveyV2.AssignedSurveyDto | undefined
  >(selectedSurvey);
  const [shouldRenderConsent, setShouldRenderConsent] = useState(
    !!selectedConsent
  );
  const [consentForAnimation, setConsentForAnimation] = useState<
    ConsentGoalDto | undefined
  >(selectedConsent);

  useEffect(() => {
    if (selectedNeedId) {
      setShouldRenderNeed(true);
      setNeedIdForAnimation(selectedNeedId);
    }
    if (selectedSurvey) {
      setShouldRenderSurvey(true);
      setSurveyForAnimation(selectedSurvey);
    }
    if (selectedConsent) {
      setShouldRenderConsent(true);
      setConsentForAnimation(selectedConsent);
    }
  }, [selectedNeedId, selectedSurvey, selectedConsent]);

  if (shouldRenderNeed && needIdForAnimation) {
    return (
      <TrackingProvider contextName="view-need-from-call-goals-panel">
        <NeedDetailContent
          needId={needIdForAnimation}
          open={!!selectedNeedId}
          onClose={() => setSelectedNeedId(undefined)}
          onAnimationComplete={() => {
            setShouldRenderNeed(false);
            setNeedIdForAnimation(undefined);
          }}
        />
      </TrackingProvider>
    );
  }

  if (shouldRenderSurvey && surveyForAnimation) {
    return (
      <TrackingProvider contextName="update-survey-from-call">
        <SurveyDrawer
          survey={surveyForAnimation}
          open={!!selectedSurvey}
          onClose={() => {
            setSelectedSurvey(undefined);
          }}
          onAnimationComplete={() => {
            setShouldRenderSurvey(false);
            setSurveyForAnimation(undefined);
          }}
        />
      </TrackingProvider>
    );
  }
  if (shouldRenderConsent && consentForAnimation) {
    return (
      <TrackingProvider contextName="create-consents-from-call">
        <ConsentDrawer
          selectedConsent={consentForAnimation}
          onClose={() => {
            setSelectedConsent(undefined);
          }}
          open={!!selectedConsent}
          onAnimationComplete={() => {
            setShouldRenderConsent(false);
            setConsentForAnimation(undefined);
          }}
        />
      </TrackingProvider>
    );
  }
  if (openAddNeed) {
    return (
      <TrackingProvider contextName="create-or-update-need-from-call">
        <AddNeedContent
          open={openAddNeed}
          onClose={() => setOpenAddNeed(false)}
        />
      </TrackingProvider>
    );
  }

  return (
    <>
      <FlexContainer
        borderRadius="lessRounded"
        direction="column"
        gap={8}
        padding={3}
        width="100%"
        height={'60%'}
        backgroundColor={ReemaThemeV2.LIGHT_MODE.palette.greyShades[100]}
        border={`1px solid ${ReemaThemeV2.LIGHT_MODE.palette.greyShades[250]}`}
      >
        <GoalsPanelContent
          selectedMember={selectedMember}
          goals={goals}
          goalsLoadingStatus={goalsLoadingStatus}
          onGoalClick={onGoalClick}
          onAddNeedClick={() => setOpenAddNeed(true)}
        />
      </FlexContainer>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          width: '100%',
          flexDirection: 'column',
        }}
      >
        <Button variant="outlined" onClick={onSaveClick}>
          Save & Close
        </Button>
      </Box>
    </>
  );
};
